import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react'
import type { AppData, Island, Scenario, SessionRecord, Settings, SyncState } from '../types'
import { load, save, freshData } from '../lib/storage'
import { gradeToRating, schedule } from '../lib/srs'
import { writeSessionCookie } from '../lib/session'
import { applyRecords, pruneTombstones, recordId, type SyncRecord } from '../lib/sync'
import { languageFromHost } from '../lib/languages'
import { levelFor, splitByLevel } from '../lib/levels'

type Action =
  | { type: 'replaceAll'; data: AppData }
  | { type: 'reset' }
  | { type: 'updateSettings'; patch: Partial<Settings> }
  | { type: 'upsertIsland'; island: Island }
  | { type: 'deleteIsland'; id: string }
  | { type: 'upsertScenario'; scenario: Scenario }
  | { type: 'recordSession'; record: SessionRecord; advanceSrs: boolean }
  | { type: 'recordScenarioRun'; scenarioId: string; score: number }
  | { type: 'patchSync'; patch: Partial<SyncState> }
  | { type: 'mergeRemote'; records: SyncRecord[]; cursor: number }
  | { type: 'signOut' }

/** History is capped so a long-lived install can't outgrow localStorage. */
const MAX_HISTORY = 500

// Exported for tests. The reducer is where the rules that can destroy a
// learner's work actually live — a listen must not overwrite a score, a
// sign-out must forget the account — and driving those through the UI proves
// far less than calling them directly.
export function reducer(state: AppData, action: Action): AppData {
  switch (action.type) {
    case 'replaceAll': {
      /*
       * An import carries the settings of wherever it was exported from, which
       * includes which language was active. Restoring a backup made on the
       * Italian site while sitting on the Mandarin one would therefore switch
       * the app to Italian and show Italian islands under a Mandarin hostname.
       *
       * The host is the authority everywhere else — load(), mergeRemote — and
       * this was the one path that forgot.
       */
      const host = languageFromHost()
      if (!host) return action.data
      return { ...action.data, settings: { ...action.data.settings, activeLanguage: host } }
    }

    case 'reset':
      return freshData()

    case 'updateSettings':
      return {
        ...state,
        settings: { ...state.settings, ...action.patch },
        settingsUpdatedAt: Date.now(),
      }

    case 'upsertIsland': {
      // Stamped here rather than at each call site, so the "every mutation
      // bumps the clock" rule can't be forgotten by a future caller.
      const island = { ...action.island, updatedAt: Date.now() }
      const exists = state.islands.some((i) => i.id === island.id)
      const islands = exists
        ? state.islands.map((i) => (i.id === island.id ? island : i))
        : [...state.islands, island]
      return {
        ...state,
        islands,
        // Re-creating a deleted island must clear its tombstone, or the merge
        // rules will delete it again on the next pull.
        tombstones: state.tombstones.filter((t) => t.recordId !== recordId('island', island.id)),
      }
    }

    case 'deleteIsland': {
      const now = Date.now()
      return {
        ...state,
        islands: state.islands.filter((i) => i.id !== action.id),
        // Beats pointing at a deleted island would strand a scenario mid-run.
        scenarios: state.scenarios.map((s) => {
          const beats = s.beats.filter((b) => b.expectIslandId !== action.id)
          return beats.length === s.beats.length ? s : { ...s, beats, updatedAt: now }
        }),
        // Without a tombstone the next pull from another device would restore it.
        tombstones: [
          ...state.tombstones.filter((t) => t.recordId !== recordId('island', action.id)),
          { recordId: recordId('island', action.id), deletedAt: now },
        ],
      }
    }

    case 'upsertScenario': {
      const scenario = { ...action.scenario, updatedAt: Date.now() }
      const exists = state.scenarios.some((s) => s.id === scenario.id)
      const scenarios = exists
        ? state.scenarios.map((s) => (s.id === scenario.id ? scenario : s))
        : [...state.scenarios, scenario]
      return { ...state, scenarios }
    }

    case 'recordSession': {
      /*
       * A listen is exposure, not an attempt.
       *
       * `advanceSrs` guarded only the schedule while the stats below it were
       * written unconditionally — and a listen records a score of 0. So playing
       * back an island you had drilled to AUTOMATIC set lastScore to 0 and
       * dropped its badge to WARMING. The schedule was protected exactly as the
       * comment promised; the numbers the badge actually reads were not.
       *
       * Worse on an account with two devices: the write also stamped
       * updatedAt, making the damaged copy the newest, so last-write-wins
       * carried it to the phone as well. The mobile app fixed this first; this
       * is the same fix, and until now the web could undo it.
       *
       * The island is left completely alone when nothing was scored. The
       * session record still syncs on its own, so the exposure is not lost.
       */
      const { record, advanceSrs } = action
      const islands = advanceSrs
        ? state.islands.map((island) => {
            if (island.id !== record.islandId) return island
            return {
              ...island,
              // The stats are part of the island record, so its clock has to
              // move or the drill result will never reach another device.
              updatedAt: record.at,
              srs: schedule(island.srs, gradeToRating(record.score)),
              stats: {
                performances: island.stats.performances + 1,
                bestScore: Math.max(island.stats.bestScore, record.score),
                lastScore: record.score,
                lastDrilledAt: record.at,
              },
            }
          })
        : state.islands
      return { ...state, islands, history: [record, ...state.history].slice(0, MAX_HISTORY) }
    }

    case 'recordScenarioRun':
      return {
        ...state,
        scenarios: state.scenarios.map((s) =>
          s.id === action.scenarioId
            ? {
                ...s,
                updatedAt: Date.now(),
                stats: {
                  runs: s.stats.runs + 1,
                  bestScore: Math.max(s.stats.bestScore, action.score),
                  lastScore: action.score,
                  lastRunAt: Date.now(),
                },
              }
            : s,
        ),
      }

    case 'patchSync':
      return { ...state, sync: { ...state.sync, ...action.patch } }

    case 'mergeRemote': {
      const merged = applyRecords(state, action.records)
      // Belt and braces: on a language subdomain the host is the authority, no
      // matter what any record claims.
      const host = languageFromHost()
      return pruneTombstones({
        ...merged,
        settings: host
          ? { ...merged.settings, activeLanguage: host }
          : merged.settings,
        sync: { ...merged.sync, cursor: action.cursor, lastSyncAt: Date.now(), lastError: null },
      })
    }

    case 'signOut':
      // Local work stays put; only the credential and the cursor are cleared,
      // so signing back in re-pushes anything that never made it up.
      return {
        ...state,
        sync: {
          ...state.sync,
          token: null,
          deviceId: null,
          deviceName: null,
          accountEmail: null,
          cursor: 0,
          lastPushedAt: 0,
          // Whatever this device learnt belonged to the account it just left.
          // The next account has to be pulled before anything is pushed at it.
          lastSyncAt: null,
          lastError: null,
        },
      }

    default:
      return state
  }
}

interface StoreValue {
  data: AppData
  dispatch: React.Dispatch<Action>
}

const StoreContext = createContext<StoreValue | null>(null)

export function StoreProvider({ children }: { children: ReactNode }) {
  const [data, dispatch] = useReducer(reducer, undefined, load)

  useEffect(() => {
    save(data)
  }, [data])

  // Mirror the token to a cookie the sibling subdomains can read, so signing in
  // on one language signs you in on all of them — and signing out clears it
  // everywhere rather than leaving four stale sessions behind.
  useEffect(() => {
    writeSessionCookie(data.sync.token)
  }, [data.sync.token])

  useEffect(() => {
    document.documentElement.dataset.theme = data.settings.theme
  }, [data.settings.theme])

  const value = useMemo(() => ({ data, dispatch }), [data])
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>')
  return ctx
}

export function useSettings(): Settings {
  return useStore().data.settings
}

export function useIsland(id: string | null): Island | undefined {
  const { data } = useStore()
  return data.islands.find((i) => i.id === id)
}

/**
 * Islands in the language currently selected.
 *
 * Views must use this rather than `data.islands`. Reading the full list shows
 * five languages at once — which is what happened when only the rail was
 * filtered — and would let an ambush fire a German block during a Mandarin
 * session.
 */
export function useActiveIslands(): Island[] {
  const { data } = useStore()
  return data.islands.filter((i) => i.language === data.settings.activeLanguage)
}

/**
 * The islands the app is willing to put in front of you unasked.
 *
 * Listing an island and assigning it are different things. `useActiveIslands`
 * is the library and stays complete — everything opens, everything drills. This
 * is the narrower set the app *chooses* from when nobody asked for a particular
 * island: what is due today, what an ambush fires at you. Telling a beginner
 * that twenty-two islands are due, ten of them past what they can say, is the
 * same discouragement the level setting exists to remove.
 */
export function useDrillableIslands(): Island[] {
  const { data } = useStore()
  const active = data.islands.filter((i) => i.language === data.settings.activeLanguage)
  // `ready` excludes both what is above the learner and the beginner
  // versions they have outgrown, so neither is ever assigned.
  return splitByLevel(active, levelFor(data.settings, data.settings.activeLanguage)).ready
}

/** Scenarios in the language currently selected. Same rule as islands. */
export function useActiveScenarios(): Scenario[] {
  const { data } = useStore()
  return data.scenarios.filter((s) => s.language === data.settings.activeLanguage)
}

export function useScenario(id: string | null): Scenario | undefined {
  const { data } = useStore()
  return data.scenarios.find((s) => s.id === id)
}
